import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

type SimpleTooltipProps = {
  content: React.ReactNode;
  children: React.ReactElement;
  sideOffset?: TooltipPrimitive.TooltipContentProps['sideOffset'];
  side?: TooltipPrimitive.TooltipContentProps['side'];
  delayDuration?: number;
  preventClose?: boolean;
};

type DismissibleTooltipProps = {
  content: React.ReactNode;
  children: React.ReactElement;
  storageKey: string;
  sideOffset?: TooltipPrimitive.TooltipContentProps['sideOffset'];
  side?: TooltipPrimitive.TooltipContentProps['side'];
  delayDuration?: number;
  gotItLabel?: string;
};

function readDismissedTooltip(storageKey: string) {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

function writeDismissedTooltip(storageKey: string) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, 'true');
  } catch {
    // Ignore storage failures; tooltip may reappear on next visit.
  }
}

const dismissalListeners = new Map<string, Set<() => void>>();

function subscribeDismissal(storageKey: string, listener: () => void) {
  if (!dismissalListeners.has(storageKey)) {
    dismissalListeners.set(storageKey, new Set());
  }
  dismissalListeners.get(storageKey)!.add(listener);
  return () => {
    dismissalListeners.get(storageKey)?.delete(listener);
  };
}

function notifyDismissal(storageKey: string) {
  dismissalListeners.get(storageKey)?.forEach((listener) => listener());
}

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-64 overflow-hidden rounded-md border bg-white px-3 py-1.5 text-sm text-prompt-safecomvulcan shadow-md',
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const SimpleTooltip = ({
  content,
  children,
  sideOffset = 4,
  side = 'top',
  delayDuration = 50,
  preventClose,
}: SimpleTooltipProps) => (
  <Tooltip delayDuration={delayDuration}>
    <TooltipTrigger
      onClick={(event) => {
        if (preventClose) {
          event.preventDefault();
        }
      }}
      onPointerDown={(event) => {
        if (preventClose) {
          event.preventDefault();
        }
      }}
      asChild
    >
      {children}
    </TooltipTrigger>
    <TooltipContent
      onPointerDownOutside={(event) => {
        if (preventClose) {
          event.preventDefault();
        }
      }}
      sideOffset={sideOffset}
      side={side}
    >
      {content}
    </TooltipContent>
  </Tooltip>
);

const DismissibleTooltip = ({
  content,
  children,
  storageKey,
  sideOffset = 4,
  side = 'top',
  delayDuration = 0,
  gotItLabel = 'Got it',
}: DismissibleTooltipProps) => {
  const dismissed = React.useSyncExternalStore(
    (onStoreChange) => subscribeDismissal(storageKey, onStoreChange),
    () => readDismissedTooltip(storageKey),
    () => false,
  );

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    writeDismissedTooltip(storageKey);
    notifyDismissal(storageKey);
  };

  if (dismissed) {
    return children;
  }

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        sideOffset={sideOffset}
        side={side}
        className="pointer-events-auto max-w-xs px-3 py-2"
      >
        <p>{content}</p>
        <button
          type="button"
          className="mt-2 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          onClick={handleDismiss}
        >
          {gotItLabel}
        </button>
      </TooltipContent>
    </Tooltip>
  );
};

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  SimpleTooltip,
  DismissibleTooltip,
};
